"use client"

import React, { useState, useEffect, useCallback } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Star,
  Clock,
  Package,
  ToggleRight,
  X
} from "lucide-react"
import { foodItemsAPI, restaurantAPI } from "../services/api"
import toast from "react-hot-toast"
import FoodItemModal from "../components/FoodItemModal"

interface FoodItem {
  _id: string
  name: string
  description: string
  image: string
  price: number
  originalPrice?: number
  category: string
  restaurant: {
    _id: string
    name: string
    image: string
    address: any
  }
  ingredients: string[]
  allergens: string[]
  nutritionalInfo: {
    calories?: number
    protein?: number
    carbs?: number
    fat?: number
  }
  rating: {
    average: number
    count: number
  }
  tags: string[]
  isAvailable: boolean
  isFeatured: boolean
  isPopular: boolean
  preparationTime: string
  createdAt: string
  updatedAt: string
}

interface Restaurant {
  _id: string
  name: string
  image: string
}

const FoodItemsPage: React.FC = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    restaurant: "",
    category: "",
    isAvailable: "",
    isFeatured: "",
    isPopular: "",
    minPrice: "",
    maxPrice: "",
    sortBy: "createdAt",
    sortOrder: "desc"
  })

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<FoodItem | null>(null)
  const [viewingItem, setViewingItem] = useState<FoodItem | null>(null)

  const queryClient = useQueryClient()

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: searchTerm,
        page: 1 // Reset to first page when search changes
      }))
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  // Fetch food items with proper error handling
  const { 
    data: foodItemsResponse, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ["foodItems", filters],
    queryFn: () => foodItemsAPI.getFoodItems(filters),
    retry: 2,
    retryDelay: 1000
  })

  // Fetch restaurants for filter dropdown
  const { data: restaurantsResponse } = useQuery({
    queryKey: ["restaurants-list"],
    queryFn: () => restaurantAPI.getRestaurants({ limit: 100 }),
  })

  // Fetch stats
  const { data: statsResponse } = useQuery({
    queryKey: ["foodItems-stats"],
    queryFn: () => foodItemsAPI.getStats(),
  })

  // Extract data with proper fallbacks
  const foodItems = foodItemsResponse?.data?.data?.foodItems || []
  
  const pagination = foodItemsResponse?.data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 20
  }
  const restaurants = restaurantsResponse?.data?.restaurants || []
  const stats = statsResponse?.data || {
    totalItems: 0,
    availableItems: 0,
    featuredItems: 0,
    popularItems: 0
  }

  // Categories
  const categories = [
    { label: "Appetizer", value: "appetizer" },
    { label: "Main Dish", value: "main-dish" },
    { label: "Side Dish", value: "side-dish" },
    { label: "Sandwich", value: "sandwich" },
    { label: "Dessert", value: "dessert" },
    { label: "Beverage", value: "beverage" },
    { label: "Drink", value: "drink" }
  ]

  // Image component with fallback
  const ImageWithFallback: React.FC<{
    src: string | undefined
    alt: string
    className: string
    fallbackSrc: string
  }> = ({ src, alt, className, fallbackSrc }) => {
    const [imgSrc, setImgSrc] = useState(src || fallbackSrc)
    const [isLoading, setIsLoading] = useState(!!src)
    const [hasError, setHasError] = useState(false)

    const handleError = () => {
      if (!hasError && imgSrc !== fallbackSrc) {
        setImgSrc(fallbackSrc)
        setHasError(true)
      }
      setIsLoading(false)
    }

    const handleLoad = () => {
      setIsLoading(false)
    }

    useEffect(() => {
      setImgSrc(src || fallbackSrc)
      setIsLoading(!!src)
      setHasError(false)
    }, [src, fallbackSrc])

    return (
      <div className={`relative ${className}`}>
        {isLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse rounded"></div>
        )}
        <img
          src={imgSrc}
          alt={alt}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
          onError={handleError}
          onLoad={handleLoad}
        />
      </div>
    )
  }

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => foodItemsAPI.deleteFoodItem(id),
    onSuccess: () => {
      toast.success("Food item deleted successfully")
      queryClient.invalidateQueries({ queryKey: ["foodItems"] })
      queryClient.invalidateQueries({ queryKey: ["foodItems-stats"] })
    },
    onError: (error: any) => {
      toast.error(error.userMessage || "Failed to delete food item")
    }
  })

  const toggleAvailabilityMutation = useMutation({
    mutationFn: (id: string) => foodItemsAPI.toggleAvailability(id),
    onSuccess: () => {
      toast.success("Availability updated successfully")
      queryClient.invalidateQueries({ queryKey: ["foodItems"] })
      queryClient.invalidateQueries({ queryKey: ["foodItems-stats"] })
    },
    onError: (error: any) => {
      toast.error(error.userMessage || "Failed to update availability")
    }
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: (data: { foodItemIds: string[], updates: any }) => 
      foodItemsAPI.bulkUpdate(data),
    onSuccess: () => {
      toast.success("Bulk update completed successfully")
      setSelectedItems([])
      queryClient.invalidateQueries({ queryKey: ["foodItems"] })
      queryClient.invalidateQueries({ queryKey: ["foodItems-stats"] })
    },
    onError: (error: any) => {
      toast.error(error.userMessage || "Failed to perform bulk update")
    }
  })

  // Event handlers
  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }))
  }

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleBulkAction = (action: string, value?: any) => {
    if (selectedItems.length === 0) {
      toast.error("Please select items first")
      return
    }

    switch (action) {
      case "delete":
        if (window.confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) {
          selectedItems.forEach(id => deleteMutation.mutate(id))
        }
        break
      case "toggleAvailability":
        selectedItems.forEach(id => toggleAvailabilityMutation.mutate(id))
        break
      case "update":
        bulkUpdateMutation.mutate({
          foodItemIds: selectedItems,
          updates: value
        })
        break
      default:
        toast.error("Invalid action")
    }
  }

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this food item?")) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleAvailability = (id: string) => {
    toggleAvailabilityMutation.mutate(id)
  }

  // Utility functions
  const formatPrice = (price: number, originalPrice?: number) => {
    if (originalPrice && originalPrice > price) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-green-600 font-semibold">{price} SDG</span>
          <span className="text-gray-400 line-through text-sm">{originalPrice} SDG</span>
        </div>
      )
    }
    return <span className="font-semibold">{price} SDG</span>
  }

  const getBadgeColor = (category: string) => {
    const colors: { [key: string]: string } = {
      "appetizer": "bg-blue-100 text-blue-800",
              "main-dish": "bg-green-100 text-green-800",
      "dessert": "bg-pink-100 text-pink-800",
      "beverage": "bg-purple-100 text-purple-800",
      "side-dish": "bg-yellow-100 text-yellow-800"
    }
    return colors[category] || "bg-gray-100 text-gray-800"
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    )
  }

  // Error state
  if (error) {
    const errorStatus = (error as any).response?.status
    const errorMessage = (error as any).userMessage || error.message
    const isAuthError = errorStatus === 401 || errorStatus === 403
    
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <p className="text-red-600 text-lg">Error loading food items</p>
          <p className="text-gray-500 text-sm mt-2">{errorMessage}</p>
          {isAuthError && (
            <p className="text-yellow-600 text-sm mt-2">
              Please check your authentication status and try refreshing the page.
            </p>
          )}
          <button 
            onClick={() => refetch()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Food Items</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage food items across all restaurants ({foodItems.length} found)
            {searchTerm && (
              <span className="ml-2 text-blue-600">
                • Searching for "{searchTerm}"
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Food Item
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Items
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.totalItems || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ToggleRight className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Available
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.availableItems || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Star className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Featured
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.featuredItems || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Star className="h-6 w-6 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Popular
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.popularItems || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
                          <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search food items..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                />
                {isLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                  </div>
                )}
                {searchTerm && !isLoading && (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Restaurant
            </label>
            <select
              value={filters.restaurant}
              onChange={(e) => handleFilterChange("restaurant", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Restaurants</option>
              {restaurants.map((restaurant: Restaurant) => (
                <option key={restaurant._id} value={restaurant._id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange("category", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              value={filters.isAvailable}
              onChange={(e) => handleFilterChange("isAvailable", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All Status</option>
              <option value="true">Available</option>
              <option value="false">Unavailable</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedItems.length} item(s) selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction("toggleAvailability")}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Toggle Availability
              </button>
              <button
                onClick={() => handleBulkAction("delete")}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Food Items Grid */}
      {foodItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {foodItems.map((item: FoodItem) => (
            <div key={item._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              {/* Image */}
              <div className="relative h-48 bg-gray-200">
                <ImageWithFallback
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  fallbackSrc="/placeholder.jpg"
                />
                <div className="absolute top-2 right-2">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item._id)}
                    onChange={() => handleSelectItem(item._id)}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                </div>
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getBadgeColor(item.category)}`}>
                    {item.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                    {item.name}
                  </h3>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.rating?.average?.toFixed(1) || "0.0"}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {item.description}
                </p>

                <div className="flex justify-between items-center mb-3">
                  <div className="text-lg font-bold text-green-600">
                    {formatPrice(item.price, item.originalPrice)}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{item.preparationTime || "15-20 min"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">
                    {item.restaurant?.name || "Unknown Restaurant"}
                  </span>
                  <div className="flex items-center gap-1">
                    {item.isFeatured && (
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                        Featured
                      </span>
                    )}
                    {item.isPopular && (
                      <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                        Popular
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewingItem(item)}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    <Eye className="w-4 h-4 inline mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => setEditingItem(item)}
                    className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    <Edit className="w-4 h-4 inline mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleAvailability(item._id)}
                    className={`px-3 py-2 text-sm rounded ${
                      item.isAvailable
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-red-100 text-red-700 hover:bg-red-200"
                    }`}
                  >
                    {item.isAvailable ? "Available" : "Unavailable"}
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No food items found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm || filters.restaurant || filters.category || filters.isAvailable
              ? "Try adjusting your filters or search terms"
              : "Get started by creating your first food item"}
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Food Item
          </button>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{" "}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalItems)} of{" "}
            {pagination.totalItems} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleFilterChange("page", pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 rounded">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handleFilterChange("page", pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {isCreateModalOpen && (
        <FoodItemModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          mode="create"
        />
      )}

      {editingItem && (
        <FoodItemModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          mode="edit"
          foodItem={editingItem}
        />
      )}

      {/* View Modal */}
      {viewingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {viewingItem.name}
                </h2>
                <button
                  onClick={() => setViewingItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ImageWithFallback
                    src={viewingItem.image}
                    alt={viewingItem.name}
                    className="w-full h-64 object-cover rounded-lg"
                    fallbackSrc="/placeholder.jpg"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Description</h3>
                    <p className="text-gray-600 dark:text-gray-400">{viewingItem.description}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Price</h3>
                    <div className="text-2xl font-bold text-green-600">
                      {formatPrice(viewingItem.price, viewingItem.originalPrice)}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Restaurant</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {viewingItem.restaurant?.name || "Unknown Restaurant"}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Category</h3>
                    <span className={`px-2 py-1 text-sm font-medium rounded ${getBadgeColor(viewingItem.category)}`}>
                      {viewingItem.category}
                    </span>
                  </div>

                  {viewingItem.ingredients && viewingItem.ingredients.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Ingredients</h3>
                      <div className="flex flex-wrap gap-1">
                        {viewingItem.ingredients.map((ingredient, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingItem.allergens && viewingItem.allergens.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Allergens</h3>
                      <div className="flex flex-wrap gap-1">
                        {viewingItem.allergens.map((allergen, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded"
                          >
                            {allergen}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Rating</h3>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span>{viewingItem.rating?.average?.toFixed(1) || "0.0"}</span>
                        <span className="text-gray-500">({viewingItem.rating?.count || 0} reviews)</span>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Preparation Time</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {viewingItem.preparationTime || "15-20 min"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FoodItemsPage
